import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';

import createFuzzySearch from '@nozbe/microfuzz';
import { SkillIcon } from 'app/components/DetailsPages/shared/CommonGround/Icons';
import useProfileEditorSkillsDrawer from 'app/components/ProfileEditor/hooks/useProfileEditorSkillsDrawer';
import { CloseIcon, SearchIconSmall, SpinnerDarkIcon } from 'app/components/shared/icons';
import { useFormikContext } from 'formik';
import { AnimatePresence } from 'framer-motion';
import useOnClickOutside from 'hooks/click-outside';
import useDebounce from 'hooks/useDebounce';
import { createSkill } from 'services/profile-editor';
import { ThemeProvider } from 'styled-components';

import {
  SkillSelectOptionContainer,
  SkillSelectOptionLabel,
  SkillSelectOptionsContainer,
  SkillSelectSelectedSkillsContainer,
  SkillSelectSelectedSkillContainer,
  SkillSelectSelectedSkillLabel,
  SkillSelectWrapper,
  SkillSelectSelectedSkillRemoveContainer,
  SkillSelectInput,
  SkillSelectOptionAdd,
  SkillSelectIconContainer,
  SkillSelectOptionAlreadyAdded,
} from './styled';

interface SkillSelectProps {
  name?: string;

  placeholder?: string;

  onChange?: (values: any[]) => void;

  allowMultiple?: boolean;

  allowCreate?: boolean;

  preselectedValues?: any[];
}

export default function SkillSelect({
  placeholder = 'No skill selected',
  name,
  onChange,
  allowMultiple = false,
  allowCreate = false,
  preselectedValues = [],
}: SkillSelectProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const formikContext = useFormikContext();
  const { allSkills } = useProfileEditorSkillsDrawer();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedSkills, setSelectedSkills] = useState<any[]>(preselectedValues);
  const [currentInputValue, setCurrentInputValue] = useState<string>('');
  const [currentlySubmitting, setCurrentlySubmitting] = useState<boolean>(false);
  const [list, setList] = useState<any[]>([]);
  const debouncedValue = useDebounce(currentInputValue);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && currentInputValue.trim() === '' && selectedSkills.length > 0) {
      const lastSkill = selectedSkills[selectedSkills.length - 1];
      handleSkillClick(lastSkill);
    }
  };

  const handleCreateSkill = async (params) => {
    const response = await createSkill(params);
    return response.data;
  };

  const { mutate } = useMutation(handleCreateSkill, {
    onMutate: () => setCurrentlySubmitting(true),
    onSettled: (data) => {
      setCurrentlySubmitting(false);
      handleSkillClick(data);
    },
  });

  const handleSkillClick = (skill) => {
    let finalSkills = [];

    const skillIndex = selectedSkills.findIndex(
      (selectedSkill) => selectedSkill.uuid === skill.uuid
    );

    finalSkills =
      skillIndex === -1
        ? allowMultiple
          ? [...selectedSkills, skill]
          : [skill]
        : selectedSkills.filter((selectedSkill) => selectedSkill.uuid !== skill.uuid);

    setSelectedSkills(finalSkills);

    if (name) {
      formikContext.setFieldValue(
        name,
        finalSkills.map((skill) => skill.uuid)
      );
    }

    setCurrentInputValue('');
    onChange?.(finalSkills);
    setIsOpen(false);
  };

  useOnClickOutside(wrapperRef, () => {
    if (isOpen) {
      formikContext.setFieldTouched(name, true, false);
    }
    setIsOpen(false);
  });

  const fuzzySearch = useMemo(() => {
    if (allSkills) {
      return createFuzzySearch(allSkills, { key: 'name' });
    }
  }, [allSkills]);

  const fuzzySearchCallback = useCallback(
    (value) => {
      if (fuzzySearch) {
        return fuzzySearch(value).map((result) => result.item);
      }
    },
    [debouncedValue, fuzzySearch]
  );

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInputValue(e.target.value);
  };

  const handleWrapperClick = () => {
    if (isOpen) return;

    inputRef.current.focus();
    setIsOpen(true);
  };

  useEffect(() => {
    if (fuzzySearch && debouncedValue.trim() !== '') {
      setList(fuzzySearchCallback(debouncedValue));
    } else {
      setList(allSkills);
    }
  }, [debouncedValue, fuzzySearchCallback, allSkills]);

  const handleSkillRemoveClick = (skill: any, e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    const finalSkills = selectedSkills.filter((selectedSkill) => selectedSkill.uuid !== skill.uuid);

    setSelectedSkills(finalSkills);

    if (name) {
      formikContext.setFieldValue(
        name,
        finalSkills.map((skill) => skill.uuid)
      );
    }

    if (isOpen) {
      setIsOpen(false);
    }
    onChange?.(finalSkills);
  };

  const newSkill = useMemo(() => {
    if (
      allowCreate &&
      debouncedValue &&
      !allSkills.find((opt) => opt.name.toLowerCase() === debouncedValue.toLowerCase())
    ) {
      return { value: debouncedValue, name: debouncedValue, isCreated: true };
    }

    return null;
  }, [debouncedValue, allowCreate, allSkills]);

  const handleNewSkillClick = (skill) => {
    mutate(skill);
    setCurrentInputValue('');
    setIsOpen(false);
  };

  const isError = useMemo<boolean>(() => {
    return formikContext.errors[name] && formikContext.touched[name];
  }, [formikContext.errors[name], formikContext.touched[name], name]);

  const isSkillSelected = useCallback(
    (skill) => {
      return selectedSkills.find((selectedSkill) => selectedSkill.uuid === skill.uuid);
    },
    [selectedSkills]
  );

  return (
    <ThemeProvider theme={{ isError }}>
      <SkillSelectWrapper ref={wrapperRef} onClick={handleWrapperClick}>
        <input type="hidden" name={name} value={formikContext.values[name]} />
        <SkillSelectSelectedSkillsContainer>
          {selectedSkills.map((skill) => (
            <SkillSelectSelectedSkillContainer key={skill.uuid}>
              <SkillIcon width={16} height={16} fill={'var(--color-data-skill-foreground)'} />
              <SkillSelectSelectedSkillLabel>{skill.name}</SkillSelectSelectedSkillLabel>
              <SkillSelectSelectedSkillRemoveContainer
                data-action="remove"
                onClick={(e) => handleSkillRemoveClick(skill, e)}
              >
                <CloseIcon width={9} height={9} fill="var(--color-text)" fillOpacity="1" />
              </SkillSelectSelectedSkillRemoveContainer>
            </SkillSelectSelectedSkillContainer>
          ))}
          {currentlySubmitting ? (
            <SkillSelectSelectedSkillContainer key={'new-skill'}>
              <SkillIcon width={16} height={16} fill={'var(--color-data-skill-foreground)'} />
              <SkillSelectSelectedSkillLabel loading>
                <SpinnerDarkIcon />
              </SkillSelectSelectedSkillLabel>
            </SkillSelectSelectedSkillContainer>
          ) : null}
          <SkillSelectInput
            ref={inputRef}
            placeholder={selectedSkills.length > 0 || currentlySubmitting ? null : placeholder}
            value={currentInputValue}
            onChange={handleOnChange}
            onKeyDown={handleKeyDown}
          />
        </SkillSelectSelectedSkillsContainer>
        <SkillSelectIconContainer>
          <SearchIconSmall width={24} height={24} fill="var(--color-primary)" />
        </SkillSelectIconContainer>
        <AnimatePresence>
          {isOpen ? (
            <SkillSelectOptionsContainer>
              {newSkill ? (
                <SkillSelectOptionContainer
                  key={newSkill.value}
                  onClick={() => handleNewSkillClick(newSkill)}
                >
                  <SkillSelectOptionLabel>{newSkill.name}</SkillSelectOptionLabel>
                  <SkillSelectOptionAdd>ADD NEW</SkillSelectOptionAdd>
                </SkillSelectOptionContainer>
              ) : null}
              {list?.map((skill) => (
                <SkillSelectOptionContainer
                  key={skill.uuid}
                  onClick={() => handleSkillClick(skill)}
                >
                  <SkillSelectOptionLabel alreadyAdded={isSkillSelected(skill)}>
                    {skill.name}
                  </SkillSelectOptionLabel>
                  {isSkillSelected(skill) ? (
                    <SkillSelectOptionAlreadyAdded>ALREADY ADDED</SkillSelectOptionAlreadyAdded>
                  ) : null}
                </SkillSelectOptionContainer>
              ))}
            </SkillSelectOptionsContainer>
          ) : null}
        </AnimatePresence>
      </SkillSelectWrapper>
    </ThemeProvider>
  );
}
